<template>
  <AppLayout>
    <div class="flex flex-col items-center gap-4">
      <h1>Organizations</h1>

      <div class="flex flex-col gap-4 w-full max-w-4xl">
        <p class="text-sm text-surface-500">
          Capture presets for the iOS app. A non-empty list locks the capture screen of that
          organization: the mode carousel and tool buttons are replaced by this fixed row of
          buttons. An empty list (<code>[]</code>) leaves the app unrestricted.
        </p>

        <div>
          <label for="org-select" class="block text-sm font-medium mb-2">Organization</label>
          <Select
            id="org-select"
            v-model="selectedOrgId"
            :options="organizations"
            optionLabel="name"
            optionValue="id"
            placeholder="Select an organization"
            class="w-full"
            filter
            :loading="loadingOrgs"
            @change="onOrgChange"
          >
            <template #option="{ option }">
              <div>
                {{ option.name }}
                <span class="text-surface-400 ml-2">{{ option.code }}</span>
              </div>
            </template>
          </Select>
        </div>

        <Textarea
          v-model="presetsJson"
          :disabled="!selectedOrgId || loading"
          class="w-full font-mono text-sm"
          style="height: 45vh"
          spellcheck="false"
          :placeholder="selectedOrgId ? 'Loading...' : 'Select an organization first'"
        />

        <div class="flex items-center gap-4">
          <Button
            label="Save"
            :loading="saving"
            :disabled="!selectedOrgId || loading || !isDirty"
            @click="save"
          />
          <span v-if="isDirty" class="text-sm text-amber-500">Unsaved changes</span>
          <span v-else-if="selectedOrgId && !loading" class="text-sm text-surface-400">
            {{ presetCount }} preset{{ presetCount === 1 ? '' : 's' }} saved
          </span>
        </div>

        <details class="border border-surface-200 dark:border-surface-700 rounded-lg p-3">
          <summary class="cursor-pointer text-sm font-medium select-none">Format</summary>
          <div class="mt-3 flex flex-col gap-3 text-sm text-surface-500">
            <p>
              A JSON array. <code>code</code> is lowercase <code>a-z0-9-</code>, unique within the
              organization, and stable — it is the analytics key stored on every shot.
              <code>label</code> is at most 24 characters. <code>icon</code> is an asset/internal
              icon key or an SF Symbol name; unknown keys fall back to a default icon.
              <code>iconColor</code> is optional (<code>#RRGGBB</code>, <code>#RRGGBBAA</code> or
              <code>transparent</code>) and defaults to the preset's own background colour.
            </p>
            <p>
              There are two kinds of preset. A <strong>composed preset</strong> carries
              <code>options</code>: a full processing configuration with <code>common</code> plus
              exactly one mode key (<code>product</code>, <code>fashion</code>,
              <code>virtualModel</code>, <code>virtualScene</code>, <code>book</code>) and no
              <code>request</code> block. A <strong>recipe preset</strong> has no
              <code>options</code> at all — its <code>code</code> must match a recipe implemented in
              the backend, which then decides what the shot does. Either way the app never sees the
              configuration: it sends back the code and the server resolves it, so resolution and
              output format are fixed per preset.
            </p>
            <pre
              class="font-mono text-xs bg-surface-100 dark:bg-surface-800 rounded p-3 overflow-x-auto"
            >{{ formatExample }}</pre>
            <p>
              To author <code>options</code>, either write it by hand against the shape above, or
              take one shot in the app with the settings you want and copy the
              <code>options</code> column of its <code>processing_events</code> row.
            </p>
            <p v-if="recipes.length">
              Registered recipes:
              <template v-for="(recipe, index) in recipes" :key="recipe.code">
                <code>{{ recipe.code }}</code
                ><span v-if="recipe.iconColor"> ({{ recipe.iconColor }})</span
                ><span v-if="index < recipes.length - 1">, </span>
              </template>
            </p>
            <p v-else-if="!loadingRecipes">
              No recipe presets are registered in this backend — every preset needs its own
              <code>options</code>.
            </p>
          </div>
        </details>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import AppLayout from '@/components/AppLayout.vue';
import { useApiClient } from '@/api/apiClient';
import type {
  CapturePresetConfigDto,
  CapturePresetRecipeDto,
  OrganizationDto,
} from '@/api/generated';
import Select from 'primevue/select';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';

const { api } = useApiClient();
const toast = useToast();
const route = useRoute();
const router = useRouter();

const formatExample = `[
  {
    "code": "demo-white-bg",
    "label": "WHITE",
    "icon": "ShootingBackground"
  },
  {
    "code": "transparent",
    "label": "CUTOUT",
    "icon": "ShootingBackground",
    "options": {
      "common": { "outputFormat": "jpeg_or_png", "resolution": "2K" },
      "product": {
        "aiMagic": "on",
        "shadow": "off",
        "backgroundOn": true,
        "backgroundValue": "transparent",
        "paddingOn": false
      }
    }
  }
]`;

const organizations = ref<OrganizationDto[]>([]);
const recipes = ref<CapturePresetRecipeDto[]>([]);
const loadingRecipes = ref(true);
const selectedOrgId = ref<string | null>(
  typeof route.query.org === 'string' ? route.query.org : null,
);
const presetsJson = ref('');
const savedJson = ref('');
const presetCount = ref(0);
const loadingOrgs = ref(true);
/** Bumped by every load; only the newest one may write to the editor. */
let loadGeneration = 0;
// Starts true when ?org= preselects an organization: until its presets arrive the textarea
// is empty, and an empty textarea that accepts typing is a Save away from wiping that
// organization's presets.
const loading = ref(selectedOrgId.value !== null);
const saving = ref(false);

const isDirty = computed(() => presetsJson.value !== savedJson.value);

/** NestJS validation failures come back as `{ message: string | string[] }`. */
function errorDetail(error: unknown): string {
  const message = (error as { message?: unknown } | null)?.message;
  if (Array.isArray(message)) return message.join('\n');
  if (typeof message === 'string') return message;
  return 'Unexpected error';
}

async function loadOrganizations() {
  loadingOrgs.value = true;
  try {
    const { data, error } = await api.adminOrganizationControllerList();
    if (error || !data) {
      throw new Error('Failed to load organizations');
    }
    organizations.value = [...data].sort((a, b) => a.name.localeCompare(b.name));
  } catch {
    toast.add({ severity: 'error', summary: 'Failed to load organizations', life: 5000 });
  } finally {
    loadingOrgs.value = false;
  }
}

async function loadRecipes() {
  try {
    const { data, error } = await api.adminCapturePresetRecipeControllerList();
    if (error || !data) {
      throw new Error('Failed to load capture preset recipes');
    }
    recipes.value = data;
  } catch {
    // Only the Format help degrades; the editor itself stays usable
    toast.add({ severity: 'warn', summary: 'Failed to load capture preset recipes', life: 5000 });
  } finally {
    loadingRecipes.value = false;
  }
}

async function loadSettings() {
  const orgId = selectedOrgId.value;
  if (!orgId) return;

  // Comparing the org id alone cannot tell two loads of the *same* org apart: switch
  // A → B → A and the first A response would populate the editor while the second is
  // still in flight, then overwrite whatever was typed meanwhile. Only the newest load
  // owns the editor.
  const generation = ++loadGeneration;

  loading.value = true;
  presetsJson.value = '';
  savedJson.value = '';
  presetCount.value = 0;
  try {
    const { data, error } = await api.adminOrganizationControllerGetSettings({
      path: { id: orgId },
    });
    if (error || !data) {
      throw new Error('Failed to load organization settings');
    }
    if (generation !== loadGeneration) return;

    const presets = data.capturePresets ?? [];
    presetCount.value = presets.length;
    presetsJson.value = JSON.stringify(presets, null, 2);
    savedJson.value = presetsJson.value;
  } catch {
    if (generation === loadGeneration) {
      toast.add({ severity: 'error', summary: 'Failed to load organization settings', life: 5000 });
    }
  } finally {
    if (generation === loadGeneration) {
      loading.value = false;
    }
  }
}

function onOrgChange() {
  router.replace({ query: selectedOrgId.value ? { org: selectedOrgId.value } : {} });
  loadSettings();
}

async function save() {
  const orgId = selectedOrgId.value;
  if (!orgId) return;

  let capturePresets: CapturePresetConfigDto[];
  try {
    capturePresets = JSON.parse(presetsJson.value);
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Invalid JSON',
      detail: e instanceof Error ? e.message : String(e),
      life: 8000,
    });
    return;
  }
  if (!Array.isArray(capturePresets)) {
    toast.add({ severity: 'error', summary: 'Expected a JSON array of presets', life: 8000 });
    return;
  }

  saving.value = true;
  const generation = loadGeneration;
  try {
    const { data, error } = await api.adminOrganizationControllerUpdateSettings({
      path: { id: orgId },
      body: { capturePresets },
    });
    if (error || !data) {
      // Backend validation messages are the whole point of this page — show them verbatim
      toast.add({
        severity: 'error',
        summary: 'Save rejected',
        detail: errorDetail(error),
        life: 15000,
      });
      return;
    }
    toast.add({ severity: 'success', summary: 'Capture presets saved', life: 3000 });

    // The write landed, but the editor may have moved on since: writing this org's
    // presets into another org's textarea and marking them clean is how one org's
    // configuration ends up saved onto another. A reload of the same org counts as
    // moving on too — it owns the editor now.
    if (selectedOrgId.value !== orgId || generation !== loadGeneration) return;

    const presets = data.capturePresets ?? [];
    presetCount.value = presets.length;
    presetsJson.value = JSON.stringify(presets, null, 2);
    savedJson.value = presetsJson.value;
  } catch (e) {
    // Without this the request simply vanished: no toast, and no way to tell whether
    // the write landed.
    toast.add({
      severity: 'error',
      summary: 'Failed to save capture presets',
      detail: e instanceof Error ? e.message : String(e),
      life: 8000,
    });
  } finally {
    saving.value = false;
  }
}

loadRecipes();

loadOrganizations().then(() => {
  // A reload with ?org= restores the selection, but only if the org still exists
  if (selectedOrgId.value && !organizations.value.some(o => o.id === selectedOrgId.value)) {
    selectedOrgId.value = null;
    // Nothing to load, so nothing will clear the initial `loading` — do it here or the
    // editor stays disabled until something else selects an org.
    loading.value = false;
  }
  loadSettings();
});
</script>
